import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Newspaper, Clock, ArrowLeft } from 'lucide-react'
import { apiUrl } from '../config/api'
import PullToRefresh from '../components/ui/PullToRefresh'
import Button from '../components/ui/Button'

interface Post {
  id: number
  title: string
  slug: string
  excerpt: string | null
  cover_image_url: string | null
  published_at: string
  reading_time: number | null
  category_name: string | null
}

interface PostDetail extends Post {
  content: string
  author_name: string | null
}

export default function BlogPage() {
  const { slug, postSlug } = useParams()
  const navigate = useNavigate()
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [post, setPost] = useState<PostDetail | null>(null)
  const [loadingPost, setLoadingPost] = useState(false)

  const fetchPosts = useCallback(async (pageNum = 1, append = false) => {
    if (pageNum === 1) setLoading(true)
    else setLoadingMore(true)
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(apiUrl(`/v1/community/my/posts?page=${pageNum}&per_page=10`), {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      })
      const data = await res.json()
      const fresh: Post[] = data.data || []
      const meta = data.meta || {}

      if (append) setPosts(prev => [...prev, ...fresh])
      else setPosts(fresh)
      setHasMore(meta.page < meta.last_page)
    } catch {} finally { setLoading(false); setLoadingMore(false) }
  }, [])

  useEffect(() => {
    if (!postSlug) fetchPosts(1)
  }, [fetchPosts, postSlug])

  useEffect(() => {
    if (postSlug) fetchPost(postSlug)
  }, [postSlug])

  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible' && !postSlug) { setPage(1); fetchPosts(1) }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [fetchPosts, postSlug])

  async function fetchPost(s: string) {
    setLoadingPost(true)
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(apiUrl(`/v1/community/my/posts/${s}`), {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      })
      if (res.ok) {
        const data = await res.json()
        setPost(data.data)
      }
    } catch {} finally { setLoadingPost(false) }
  }

  const handleRefresh = async () => { setPage(1); await fetchPosts(1) }

  function loadMore() {
    const next = page + 1
    setPage(next)
    fetchPosts(next, true)
  }

  function formatDate(d: string) {
    return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  // Detalhe do post
  if (postSlug) {
    if (loadingPost) {
      return (
        <div className="flex-1 overflow-y-auto p-4 md:p-6 animate-pulse">
          <div className="max-w-3xl mx-auto space-y-4">
            <div className="h-6 w-32 bg-gray-200 rounded" />
            <div className="h-48 bg-gray-100 rounded-2xl" />
            <div className="h-8 w-3/4 bg-gray-200 rounded" />
            <div className="space-y-2">
              {[1,2,3,4].map(i => <div key={i} className="h-4 bg-gray-100 rounded" />)}
            </div>
          </div>
        </div>
      )
    }

    if (!post) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-content-muted">Post não encontrado</p>
        </div>
      )
    }

    return (
      <div className="flex-1 overflow-y-auto">
        <div className="h-12 px-4 flex items-center gap-3 border-b border-border-subtle">
          <button onClick={() => navigate(`/${slug}/blog`)} className="text-content-muted hover:text-content-normal">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-base font-semibold text-content-normal truncate">Blog</h1>
        </div>

        <div className="max-w-3xl mx-auto px-4 md:px-6 py-6">
          {post.cover_image_url && (
            <img src={post.cover_image_url} alt={post.title} className="w-full max-h-72 object-cover rounded-2xl mb-6" />
          )}

          {post.category_name && (
            <span className="text-xs font-semibold text-primary uppercase tracking-wide">{post.category_name}</span>
          )}

          <h1 className="text-2xl font-bold text-content-normal mt-1 mb-3">{post.title}</h1>

          <div className="flex items-center gap-3 text-xs text-content-muted mb-6">
            {post.author_name && <span>{post.author_name}</span>}
            <span>{formatDate(post.published_at)}</span>
            {post.reading_time && (
              <span className="flex items-center gap-1"><Clock size={10} />{post.reading_time} min</span>
            )}
          </div>

          <div
            className="prose prose-sm max-w-none text-content-normal
              prose-headings:text-content-normal prose-headings:font-bold
              prose-a:text-primary prose-img:rounded-xl"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </div>
      </div>
    )
  }

  // Lista de posts
  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="h-12 px-4 items-center border-b border-border-subtle hidden lg:flex">
        <Newspaper size={20} className="text-content-muted mr-2" />
        <h1 className="text-base font-semibold text-content-normal">Blog</h1>
      </div>

      <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto w-full">

        {loading ? (
          <div className="space-y-4">
            {[1,2,3].map(i => (
              <div key={i} className="rounded-2xl border border-border-subtle p-4 animate-pulse">
                <div className="h-40 bg-gray-100 rounded-xl mb-3" />
                <div className="h-5 w-2/3 bg-gray-200 rounded mb-2" />
                <div className="h-3 w-full bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-surface-secondary flex items-center justify-center mx-auto mb-4">
              <Newspaper size={32} className="text-content-muted opacity-40" />
            </div>
            <p className="text-sm font-medium text-content-muted">Nenhuma publicação disponível</p>
            <p className="text-xs text-content-muted mt-1">Puxe pra baixo pra atualizar</p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {posts.map(p => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/${slug}/blog/${p.slug}`)}
                  className="w-full text-left rounded-2xl border border-border-subtle bg-surface-secondary shadow-sm overflow-hidden active:scale-[0.98] transition-all"
                >
                  {p.cover_image_url && (
                    <img src={p.cover_image_url} alt={p.title} className="w-full h-40 object-cover" />
                  )}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-1.5">
                      {p.category_name && (
                        <span className="text-[10px] font-semibold text-primary uppercase tracking-wide">{p.category_name}</span>
                      )}
                      <span className="text-[10px] text-content-muted">{formatDate(p.published_at)}</span>
                      {p.reading_time && (
                        <span className="flex items-center gap-0.5 text-[10px] text-content-muted">
                          <Clock size={9} />{p.reading_time} min
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm font-bold text-content-normal mb-1">{p.title}</h3>
                    {p.excerpt && (
                      <p className="text-xs text-content-muted line-clamp-2">{p.excerpt}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {hasMore && (
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="w-full py-3 mt-4 text-sm font-medium text-primary hover:bg-surface-hover rounded-xl transition-colors"
              >
                {loadingMore ? 'Carregando...' : 'Ver mais'}
              </button>
            )}
          </>
        )}
      </div>
    </PullToRefresh>
  )
}
